/**
 * Живая пиксельная матрица — мотив визуального стиля (13.09.2026, референс
 * Maple Research): поле из мелких глифов, по которому перетекают пятна шума.
 *
 * Без библиотек и без WebGL: на карточке 360×260 это ~2 500 клеток, и кадр
 * укладывается в пару миллисекунд даже на слабом Android. Цвет приходит
 * снаружи — из CSS элемента, — палитры здесь нет.
 */

const imul = Math.imul;

function hash(x: number, y: number, z: number, seed: number): number {
  let h = imul(x, 374761393) ^ imul(y, 668265263) ^ imul(z, 1274126177) ^ imul(seed, 1103515245);
  h = imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

const fade = (t: number) => t * t * (3 - 2 * t);
const mix = (a: number, b: number, t: number) => a + (b - a) * t;

/** Гладкий шум в [0, 1): соседние точки близки, одинаковые входы — одинаковый выход. */
export function noise3(x: number, y: number, z: number, seed = 0): number {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const zi = Math.floor(z);
  const u = fade(x - xi);
  const v = fade(y - yi);
  const w = fade(z - zi);
  const c = (dx: number, dy: number, dz: number) => hash(xi + dx, yi + dy, zi + dz, seed);
  return mix(
    mix(mix(c(0, 0, 0), c(1, 0, 0), u), mix(c(0, 1, 0), c(1, 1, 0), u), v),
    mix(mix(c(0, 0, 1), c(1, 0, 1), u), mix(c(0, 1, 1), c(1, 1, 1), u), v),
    w,
  );
}

/** Две октавы: крупные пятна и рваный край, как в референсе. */
export const fieldValue = (x: number, y: number, t: number, seed = 0): number =>
  noise3(x, y, t, seed) * 0.7 + noise3(x * 2.3, y * 2.3, t * 1.4, seed + 11) * 0.3;

/** Порог, ниже которого клетка пустая: пятна, а не сплошная заливка. */
export const EMPTY_BELOW = 0.47;

const DOT = 1.25;

/** Характер поля. По умолчанию — плотная матрица крупных блоков. */
export interface FieldLook {
  /** CSS-пикселей на клетку. */
  cell?: number;
  /** Размер пятен: чем меньше, тем крупнее. */
  scale?: number;
  /** Порог пустой клетки: выше — реже узор. */
  emptyBelow?: number;
  /** Скорость течения относительно обычной. */
  speed?: number;
}

// Глифы 3×3: разные, но одного веса — как буквы на матрице.
const GLYPHS: number[][] = [
  [0, 1, 0, 1, 1, 1, 0, 1, 0],
  [1, 0, 1, 0, 1, 0, 1, 0, 1],
  [1, 1, 1, 0, 0, 0, 1, 1, 1],
  [1, 0, 0, 1, 1, 0, 1, 1, 1],
  [0, 1, 1, 1, 0, 1, 1, 1, 0],
  [1, 1, 0, 0, 1, 0, 0, 1, 1],
];

/** Фон всего приложения (14.09.2026): крупные редкие пятна, текут вдвое
 *  медленнее блоков. Узор должен угадываться на чёрном, а не перетягивать
 *  взгляд с карточек — заказчик выбрал тихий вариант, а не контрастный. */
export const BACKDROP_LOOK: FieldLook = { cell: 8, scale: 0.035, emptyBelow: 0.6, speed: 0.45 };

/** Нарисовать кадр. `t` — время в секундах. */
export function drawField(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  t: number,
  seed: number,
  color: string,
  look: FieldLook = {},
): void {
  const { cell = 6, scale = 0.055, emptyBelow = EMPTY_BELOW, speed = 1 } = look;
  // Ступени яркости держат прежние отступы от порога, где бы он ни стоял.
  const dim = emptyBelow + 0.05;
  const mid = emptyBelow + 0.13;
  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = color;
  const cols = Math.ceil(width / cell);
  const rows = Math.ceil(height / cell);
  const z = t * 0.35 * speed;
  // Глиф меняется скачками, а не плавно: так мерцает матрица, а не туман.
  const tick = Math.floor(t * 3 * speed);

  for (let cy = 0; cy < rows; cy++) {
    for (let cx = 0; cx < cols; cx++) {
      const v = fieldValue(cx * scale, cy * scale, z, seed);
      if (v < emptyBelow) continue;
      const x = cx * cell;
      const y = cy * cell;
      if (v < dim) {
        ctx.globalAlpha = 0.35;
        ctx.fillRect(x + 2, y + 2, DOT, DOT);
        continue;
      }
      const glyph = GLYPHS[Math.floor(hash(cx, cy, tick, seed) * GLYPHS.length)];
      ctx.globalAlpha = v < mid ? 0.6 : 1;
      for (let i = 0; i < 9; i++) {
        if (glyph[i]) ctx.fillRect(x + (i % 3) * 1.5, y + Math.floor(i / 3) * 1.5, DOT, DOT);
      }
    }
  }
  ctx.globalAlpha = 1;
}
